// Duration preset types and calculation helpers

export interface DateRange {
  from: string;  // datetime-local format YYYY-MM-DDTHH:mm
  to: string;    // datetime-local format YYYY-MM-DDTHH:mm
}

export type DurationPreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'custom';

export const durationPresets: Array<{ value: DurationPreset; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

// Format datetime as YYYY-MM-DDTHH:mm (local time)
const formatDateTime = (date: Date, endOfDay: boolean = false): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  // Start of day: 00:00, End of day: 23:59
  const time = endOfDay ? '23:59' : '00:00';
  return `${year}-${month}-${day}T${time}`;
};

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const startOfWeek = (date: Date): Date => {
  const result = new Date(date);
  const day = result.getDay();
  // Adjust to Monday (day 1). If Sunday (0), go back 6 days
  const diff = day === 0 ? 6 : day - 1;
  result.setDate(result.getDate() - diff);
  return result;
};

export const getDurationPresetRange = (preset: DurationPreset): DateRange => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'today':
      // Today: 00:00 to 23:59
      return { from: formatDateTime(today, false), to: formatDateTime(today, true) };

    case 'yesterday': {
      const yesterday = addDays(today, -1);
      // Yesterday: 00:00 to 23:59
      return { from: formatDateTime(yesterday, false), to: formatDateTime(yesterday, true) };
    }

    case 'this_week': {
      const thisWeekStart = startOfWeek(today);
      // This week: Monday 00:00 to Today 23:59
      return { from: formatDateTime(thisWeekStart, false), to: formatDateTime(today, true) };
    }

    case 'last_week': {
      const thisWeekStart = startOfWeek(today);
      const lastWeekEnd = addDays(thisWeekStart, -1);
      const lastWeekStart = addDays(lastWeekEnd, -6);
      // Last week: Monday 00:00 to Sunday 23:59
      return { from: formatDateTime(lastWeekStart, false), to: formatDateTime(lastWeekEnd, true) };
    }

    case 'this_month': {
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      // This month: 1st 00:00 to Today 23:59
      return { from: formatDateTime(thisMonthStart, false), to: formatDateTime(today, true) };
    }

    case 'last_month': {
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      const lastMonthStart = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
      // Last month: 1st 00:00 to last day 23:59
      return { from: formatDateTime(lastMonthStart, false), to: formatDateTime(lastMonthEnd, true) };
    }

    default:
      return { from: '', to: '' };
  }
};

// Convert preset to timeRange parameter format for API
export const convertPresetToTimeRange = (preset: DurationPreset, customFrom?: string, customTo?: string): string => {
  if (preset === 'custom') {
    if (customFrom && customTo) {
      return `custom:${customFrom},${customTo}`;
    }
    return '';
  }

  const range = getDurationPresetRange(preset);
  if (range.from && range.to) {
    return `custom:${range.from},${range.to}`;
  }
  return '';
};

// Format date for display (handles both YYYY-MM-DD and YYYY-MM-DDTHH:mm formats)
const formatDisplayDate = (dateStr: string): string => {
  // Extract date part if datetime string (e.g., "2025-12-18T00:00" -> "2025-12-18")
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Get display label for a duration preset or custom range
export const getDurationLabel = (preset: DurationPreset, customFrom?: string, customTo?: string): string => {
  if (preset === 'custom' && customFrom && customTo) {
    return `${formatDisplayDate(customFrom)} - ${formatDisplayDate(customTo)}`;
  }

  const range = getDurationPresetRange(preset);
  const now = new Date();

  switch (preset) {
    case 'today':
      return `Today (${formatDisplayDate(range.from)})`;
    case 'yesterday':
      return `Yesterday (${formatDisplayDate(range.from)})`;
    case 'this_week':
      return `This Week (${formatDisplayDate(range.from)} - ${formatDisplayDate(range.to)})`;
    case 'last_week':
      return `Last Week (${formatDisplayDate(range.from)} - ${formatDisplayDate(range.to)})`;
    case 'this_month':
      return `${now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} (${formatDisplayDate(range.from)} - ${formatDisplayDate(range.to)})`;
    case 'last_month': {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return `${lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} (${formatDisplayDate(range.from)} - ${formatDisplayDate(range.to)})`;
    }
    default:
      return preset;
  }
};
